import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from './apiClient'; // central Axios instance

// Fetch all learning bundles
export const fetchLearningBundles = createAsyncThunk(
  'learningBundles/fetchLearningBundles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/learning-bundles/');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch learning bundles'
      );
    }
  }
);

// Create a new learning bundle
export const createLearningBundle = createAsyncThunk(
  'learningBundles/createLearningBundle',
  async (bundleData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/learning-bundles/', bundleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create learning bundle'
      );
    }
  }
);

// Update an existing learning bundle
export const updateLearningBundle = createAsyncThunk(
  'learningBundles/updateLearningBundle',
  async (bundleData, { rejectWithValue }) => {
    try {
      const { id, ...data } = bundleData;
      const response = await apiClient.put(`/learning-bundles/${id}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update learning bundle'
      );
    }
  }
);

// Delete a learning bundle
export const deleteLearningBundle = createAsyncThunk(
  'learningBundles/deleteLearningBundle',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/learning-bundles/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete learning bundle'
      );
    }
  }
);

// Update learning bundle status
export const updateLearningBundleStatus = createAsyncThunk(
  'learningBundles/updateLearningBundleStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/learning-bundles/${id}/status`, { status });
      return { id, status: response.data.status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update bundle status');
    }
  }
);

const learningBundlesSlice = createSlice({
  name: 'learningBundles',
  initialState: {
    bundles: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchLearningBundles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLearningBundles.fulfilled, (state, action) => {
        state.loading = false;
        state.bundles = action.payload;
      })
      .addCase(fetchLearningBundles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createLearningBundle.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLearningBundle.fulfilled, (state, action) => {
        state.loading = false;
        state.bundles.push(action.payload);
      })
      .addCase(createLearningBundle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateLearningBundle.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLearningBundle.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.bundles.findIndex(bundle => bundle.id === action.payload.id);
        if (index !== -1) {
          state.bundles[index] = action.payload;
        }
      })
      .addCase(updateLearningBundle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteLearningBundle.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLearningBundle.fulfilled, (state, action) => {
        state.loading = false;
        state.bundles = state.bundles.filter(bundle => bundle.id !== action.payload);
      })
      .addCase(deleteLearningBundle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Status update
      .addCase(updateLearningBundleStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLearningBundleStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { id, status } = action.payload;
        const index = state.bundles.findIndex(bundle => bundle.id === id);
        if (index !== -1) {
          state.bundles[index].status = status;
        }
      })
      .addCase(updateLearningBundleStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default learningBundlesSlice.reducer;
